import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, Wallet, Users, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { formatCHF } from "@/lib/format";

export default function DriverReferral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading || !data) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;

  const copyCode = () => {
    navigator.clipboard.writeText(data.code);
    toast.success("Codice copiato!");
  };

  const shareWa = () => {
    const text = encodeURIComponent(data.share_text);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const progress = Math.min(100, Math.round((data.completed_invitees / Math.max(1, data.invitees_count)) * 100));

  return (
    <div className="space-y-6" data-testid="driver-referral-page">
      <div>
        <p className="overline">Programma Driver</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Porta un driver</h1>
        <p className="text-dapnow-muted mt-2 text-sm max-w-xl">
          Invita un altro trasportatore: dopo le sue prime <strong>{data.deliveries_required}</strong> consegne completate, ricevete entrambi <strong>{formatCHF(data.bonus_amount)}</strong> di bonus.
        </p>
      </div>

      {/* Code card */}
      <div className="relative overflow-hidden rounded-3xl bg-dapnow-ink text-white p-6 sm:p-8">
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-dapnow-blue/40 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 h-64 w-64 rounded-full bg-dapnow-red/30 blur-3xl" />
        <div className="relative">
          <p className="overline text-white/70">Il tuo codice</p>
          <p className="font-display text-5xl font-extrabold tracking-tighter mt-2" data-testid="referral-code">{data.code}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={copyCode} data-testid="copy-code-btn" className="inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-4 h-11 text-sm font-semibold transition">
              <Copy className="h-4 w-4" /> Copia codice
            </button>
            <button onClick={shareWa} data-testid="share-wa-btn" className="inline-flex items-center gap-2 rounded-xl bg-white text-dapnow-ink px-4 h-11 text-sm font-semibold hover:bg-white/90 transition">
              <Share2 className="h-4 w-4" /> Condividi su WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="card-soft p-5">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><Wallet className="h-5 w-5" /></span>
          <p className="overline mt-4">Bonus accumulati</p>
          <p className="font-display text-2xl font-bold tracking-tight mt-1">{formatCHF(data.earnings_bonus)}</p>
        </div>
        <div className="card-soft p-5">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Users className="h-5 w-5" /></span>
          <p className="overline mt-4">Driver invitati</p>
          <p className="font-display text-2xl font-bold tracking-tight mt-1">{data.invitees_count}</p>
        </div>
        <div className="card-soft p-5">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><ShieldCheck className="h-5 w-5" /></span>
          <p className="overline mt-4">Bonus sbloccati</p>
          <p className="font-display text-2xl font-bold tracking-tight mt-1">{data.completed_invitees}</p>
          <div className="mt-2 h-1.5 rounded-full bg-dapnow-line overflow-hidden">
            <div className="h-full bg-dapnow-blue" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="card-soft p-5 sm:p-6">
        <h2 className="font-display text-lg font-bold tracking-tight">I driver che hai invitato</h2>
        {data.invitees.length === 0 ? (
          <p className="mt-3 text-sm text-dapnow-muted">Ancora nessuno. Condividi il tuo codice per iniziare a guadagnare.</p>
        ) : (
          <ul className="mt-3 divide-y divide-dapnow-line">
            {data.invitees.map((i) => (
              <li key={i.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="font-semibold tracking-tight">{i.name}</p>
                  <p className="text-xs text-dapnow-muted">{i.delivered_count} / {data.deliveries_required} consegne</p>
                </div>
                {i.bonus_unlocked ? (
                  <span className="pill bg-emerald-50 text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Bonus sbloccato</span>
                ) : (
                  <span className="pill bg-amber-50 text-amber-700">In progresso</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-dapnow-muted">
        I bonus vengono accreditati come saldo guadagni nella sezione <strong>Guadagni</strong> e verranno liquidati nella prossima fase con i pagamenti reali.
      </p>
    </div>
  );
}
